import { ImageOff, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { FlagIcon } from '@/components/FlagIcon';

import type { AssetResponse } from '../../api';
import { needsSourceImage } from '../../constants/taxonomy';
import { sourceImageBadge } from '../grid/sourceImageBadge';
import { smallUrlOf, type AssetUrlLookup } from '../grid/useAssetUrls';

/**
 * One media-plan row and the assets picked for it: the part the basket and a
 * saved brief show identically.
 *
 * Only the rendering is shared. Where the row comes from (a live media-plan
 * row in the basket, the copy a brief line kept), what can be done to it, and
 * what is said about it (an outcome, a warning) belong to the caller — passed
 * in as text and slots, so neither drawer has to know about the other.
 */
export function LineGroup({
  market,
  title,
  details,
  assetIds,
  assetById,
  urls,
  status,
  notice,
  assetNote,
  onRemoveLine,
  onRemoveRow,
  removeLineLabel = 'Remove from basket',
  removeDisabled = false,
}: {
  market: string | null;
  /** The row's name, e.g. its campaign code. */
  title: string;
  /** Quarter · platform · format · size, or as much of it as is known. */
  details: string;
  assetIds: string[];
  assetById: Map<string, AssetResponse>;
  urls: AssetUrlLookup;
  /** Next to the count in the header, e.g. the row's outcome. */
  status?: ReactNode;
  /** Under the header, e.g. why the row was skipped. */
  notice?: ReactNode;
  /** Under an asset's details, e.g. that its rights have expired. */
  assetNote?: (assetId: string) => ReactNode;
  /** Absent: the lines can't be removed here, so no button is shown. */
  onRemoveLine?: (assetId: string) => void;
  onRemoveRow?: () => void;
  /** What removing a line takes it out of, for its button's label. */
  removeLineLabel?: string;
  /** The remove buttons stay, greyed, while nothing may change. */
  removeDisabled?: boolean;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200">
      <header className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
        {market && <FlagIcon countryCode={market} className="h-3 w-auto shrink-0" />}
        <div className="min-w-0">
          <p className="truncate font-mono text-[11px] font-medium text-slate-600">{title}</p>
          <p className="truncate text-[11px] text-slate-400">{details}</p>
        </div>
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {status}
          <span className="rounded-full bg-white px-2 py-0.5 text-[10.5px] font-medium text-slate-500 ring-1 ring-slate-200">
            {assetIds.length}×
          </span>
        </span>
        {onRemoveRow && (
          <button
            type="button"
            onClick={onRemoveRow}
            disabled={removeDisabled}
            className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-white hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label={`Remove all assets for ${title}`}
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        )}
      </header>

      {notice}

      <ul>
        {assetIds.map((assetId) => {
          const asset = assetById.get(assetId);
          const url = smallUrlOf(urls, assetId);
          const note = assetNote?.(assetId);
          return (
            <li
              key={assetId}
              className="flex items-center gap-2.5 px-3 py-2 [&+li]:border-t [&+li]:border-slate-100"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-100">
                {url ? (
                  <img src={url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageOff className="h-3.5 w-3.5 text-slate-300" strokeWidth={1.5} />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12px] font-medium text-slate-700">
                  {asset?.filename_original ?? 'Asset unavailable'}
                </p>
                <p className="truncate text-[10.5px] text-slate-400">
                  {/* Its own code if it has one, otherwise the source images it
                      came from. Neither means the link is still owed, which is
                      worth saying here — the basket is where a selection gets
                      checked before it is acted on.
                      The badge's `title` rather than its `label`: this is a
                      sentence on a `truncate` line, not a pill, so it reads
                      "Source images: SI-000042, SI-000043" and lets the
                      overflow clip — rather than "+2", which would be a count
                      with nothing to hover for the rest.
                      Via `needsSourceImage` rather than a bare "no code, no
                      parent" test: filmic is exempt from the link, so the bare
                      test showed a blocker on an asset both publish gates let
                      through. Same rule, one definition. */}
                  {asset?.source_image_code ??
                    sourceImageBadge(asset?.source_image_codes)?.title ??
                    (asset && needsSourceImage(asset) ? 'Needs a source image' : '')}
                  {asset?.width && asset?.height ? ` · ${asset.width}×${asset.height}` : ''}
                </p>
                {note}
              </div>
              {onRemoveLine && (
                <button
                  type="button"
                  onClick={() => onRemoveLine(assetId)}
                  disabled={removeDisabled}
                  className="shrink-0 rounded p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label={removeLineLabel}
                >
                  <X className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
